/**
 * Column Picker Component
 *
 * Dropdown component for selecting visible columns in list views
 * with persistence and reset to defaults functionality
 */

import { renderIcon } from '../utils/icons';
import { preferencesStore, type ColumnPreferences, type ViewId } from '../services/preferences-store';

export interface ColumnDefinition {
  id: string;
  label: string;
  required?: boolean; // Cannot be hidden
  default?: boolean; // Visible by default
}

export interface ColumnPickerConfig {
  viewId: ViewId;
  columns: ColumnDefinition[];
  onColumnsChange?: (visibleColumns: string[]) => void;
}

export class ColumnPicker {
  private container: HTMLElement;
  private dropdown: HTMLElement | null = null;
  private visibleColumns: Set<string>;
  private defaultColumns: Set<string>;
  private isOpen = false;

  constructor(private config: ColumnPickerConfig) {
    this.container = document.createElement('div');
    this.container.className = 'relative';

    // Initialize visible columns from preferences or defaults
    const savedPrefs = preferencesStore.getColumnPreferences(config.viewId);
    if (savedPrefs?.visibleColumns) {
      this.visibleColumns = new Set(savedPrefs.visibleColumns);
    } else {
      // Use default columns (required columns or those explicitly marked as default: true)
      this.visibleColumns = new Set(
        config.columns
          .filter(col => col.required || col.default === true)
          .map(col => col.id)
      );
    }

    // Store defaults for reset functionality
    this.defaultColumns = new Set(
      config.columns
        .filter(col => col.required || col.default === true)
        .map(col => col.id)
    );

    this.render();
    this.attachEventListeners();
  }

  /**
   * Get the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Get currently visible column IDs
   */
  getVisibleColumns(): string[] {
    return Array.from(this.visibleColumns);
  }

  /**
   * Render the column picker
   */
  private render(): void {
    this.container.innerHTML = `
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium glass-button rounded-lg hover:bg-accent/10 transition-colors"
        title="Customize columns"
      >
        ${renderIcon('columns', { size: 'sm' }).outerHTML}
        <span>Columns</span>
        ${renderIcon('chevronDown', { size: 'xs' }).outerHTML}
      </button>
    `;
  }

  /**
   * Create and show dropdown
   */
  private showDropdown(): void {
    if (this.dropdown) {
      this.hideDropdown();
      return;
    }

    this.dropdown = document.createElement('div');
    this.dropdown.className = 'absolute top-full right-0 mt-1 w-64 glass rounded-lg shadow-lg z-50';

    // Header
    const header = document.createElement('div');
    header.className = 'px-3 py-2 border-b border-border';
    header.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-foreground">Show Columns</span>
        <button
          type="button"
          class="text-xs text-primary hover:text-primary/80 transition-colors"
        >
          Reset to defaults
        </button>
      </div>
    `;

    // Column list
    const list = document.createElement('div');
    list.className = 'py-1 max-h-80 overflow-y-auto';

    this.config.columns.forEach(column => {
      const item = document.createElement('label');
      item.className = 'flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 transition-colors cursor-pointer';

      const isChecked = this.visibleColumns.has(column.id);
      const isRequired = column.required;

      item.innerHTML = `
        <input
          type="checkbox"
          class="w-4 h-4 rounded border-border bg-background text-primary focus:ring-primary focus:ring-2"
          ${isChecked ? 'checked' : ''}
          ${isRequired ? 'disabled' : ''}
          data-column-id="${column.id}"
        />
        <span class="text-sm text-foreground ${isRequired ? 'opacity-60' : ''}">
          ${column.label}
          ${isRequired ? '<span class="text-xs text-muted-foreground">(required)</span>' : ''}
        </span>
      `;

      list.appendChild(item);
    });

    this.dropdown.appendChild(header);
    this.dropdown.appendChild(list);
    this.container.appendChild(this.dropdown);

    // Add event listeners to dropdown
    const resetBtn = header.querySelector('button');
    resetBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.resetToDefaults();
    });

    // Add change listeners to checkboxes
    list.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        const input = e.target as HTMLInputElement;
        const columnId = input.getAttribute('data-column-id');
        if (!columnId) return;

        if (input.checked) {
          this.visibleColumns.add(columnId);
        } else {
          this.visibleColumns.delete(columnId);
        }

        this.savePreferences();
        this.notifyChange();
      });
    });

    this.isOpen = true;
  }

  /**
   * Hide dropdown
   */
  private hideDropdown(): void {
    if (this.dropdown) {
      this.dropdown.remove();
      this.dropdown = null;
      this.isOpen = false;
    }
  }

  /**
   * Reset columns to defaults
   */
  private resetToDefaults(): void {
    this.visibleColumns = new Set(this.defaultColumns);

    // Update checkboxes
    if (this.dropdown) {
      this.dropdown.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        const columnId = checkbox.getAttribute('data-column-id');
        if (columnId) {
          (checkbox as HTMLInputElement).checked = this.visibleColumns.has(columnId);
        }
      });
    }

    // Clear saved preferences
    preferencesStore.clearColumnPreferences(this.config.viewId);
    this.notifyChange();
  }

  /**
   * Save current column preferences
   */
  private savePreferences(): void {
    const prefs: ColumnPreferences = {
      visibleColumns: Array.from(this.visibleColumns)
    };
    preferencesStore.setColumnPreferences(this.config.viewId, prefs);
  }

  /**
   * Notify parent of column changes
   */
  private notifyChange(): void {
    if (this.config.onColumnsChange) {
      this.config.onColumnsChange(Array.from(this.visibleColumns));
    }
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Toggle dropdown on button click
    const button = this.container.querySelector('button');
    button?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.isOpen) {
        this.hideDropdown();
      } else {
        this.showDropdown();
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target as Node)) {
        this.hideDropdown();
      }
    });

    // Prevent dropdown from closing when clicking inside
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.absolute')) {
        e.stopPropagation();
      }
    });
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.hideDropdown();
    this.container.remove();
  }
}